// Drop-in loading step for the brand-onboarding wizard. Use anywhere a long
// running async step (read brand, find competitors, fetch ads, build opening
// brief) needs to communicate "AI is working" without going silent on the
// user. Visually cloned from StepPreview.tsx's loading state (the sp-*
// keyframes there), retuned with a ws-* prefix so multiple animated surfaces
// can share a page without keyframe-name collisions.
//
// The progress bar follows the same exponential-approach curve used by
// StepPreview — `100 * (1 - exp(-t / tau))` where tau is chosen so the bar
// hits ~80% at the expected duration. It's capped at 92% and never snaps to
// 100 by itself; that's the caller's job (this component has no notion of
// "done", it just animates until unmounted).

interface WizardLoadingStepProps {
  /** Big headline like "Reading your brand…" */
  title: string;
  /** Optional subtext like "This usually takes 15-30 seconds" */
  subtitle?: string;
  /** Expected duration in ms for the exponential-approach progress bar (default 30000). */
  expectedDurationMs?: number;
  /** Optional staged subtitles — if provided, the visible subtext cycles
   *  through these as time elapses, surfacing what we're working on at each
   *  stage of the wait. Overrides `subtitle` when present. The stages are
   *  spaced evenly across expectedDurationMs. */
  subtitles?: string[];
  /** Jun 15 — when set, OVERRIDES the wall-clock exponential progress
   *  curve with the actual processed/total ratio. Use this for steps
   *  where the backend reports concrete progress (e.g. async product
   *  save: 199 of 359 saved). Without this, the ring and the subtitle
   *  count go out of sync because the ring tracks elapsed time while
   *  the subtitle tracks real work. Range 0..100; values are clamped. */
  progressPct?: number;
}

// Six pre-positioned sparkles. The mix of positions / delays / durations is
// intentional — if they all share the same animation timing the eye reads it
// as one synchronized swarm, which looks fake. Each sparkle has its own
// rhythm.
const SPARKLES = [
  { top: '15%', left: '20%', size: 8, delay: '0s', dur: '3.2s' },
  { top: '25%', left: '78%', size: 6, delay: '0.6s', dur: '2.8s' },
  { top: '60%', left: '12%', size: 10, delay: '1.1s', dur: '3.6s' },
  { top: '70%', left: '85%', size: 7, delay: '0.3s', dur: '3.0s' },
  { top: '40%', left: '90%', size: 5, delay: '1.4s', dur: '2.5s' },
  { top: '85%', left: '45%', size: 9, delay: '0.9s', dur: '3.4s' },
];

export default function WizardLoadingStep({
  title,
  subtitle,
  expectedDurationMs = 30000,
  subtitles,
  progressPct: progressPctOverride,
}: WizardLoadingStepProps) {
  const [progress, setProgress] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  // Jun 15 — when an explicit progressPct is supplied, we skip the
  // wall-clock interval entirely (no point doing the work) and use the
  // caller's value directly. This is what keeps the ring in sync with
  // "199 of 359 saved" instead of drifting independently.
  const hasProgressOverride = typeof progressPctOverride === 'number';

  // Jun 25 — read subtitles via ref so the timer effect doesn't re-fire
  // when the parent re-renders with a new inline array literal. Before
  // this, callers like find-opening passed `subtitles={[...]}` inline →
  // every parent re-render created a new array reference → useEffect deps
  // change → `start = Date.now()` resets → progress jumps back to 0.
  // More visible in prod where the LLM call takes 30-60s vs ~5s locally.
  const subtitlesRef = useRef(subtitles);
  subtitlesRef.current = subtitles;

  useEffect(() => {
    if (hasProgressOverride) return;
    // Same math as StepPreview's single-frame path. TAU is the time constant
    // for the exponential approach; expectedDurationMs / ln(5) lands us at
    // ~80% completion at the expected wall clock. We cap visible progress at
    // 92 so the bar never claims completion — the caller unmounts when the
    // real work resolves.
    const start = Date.now();
    const TAU = expectedDurationMs / Math.log(5);
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - start;
      const next = 100 * (1 - Math.exp(-elapsed / TAU));
      setProgress(Math.min(92, next));
      // Cycle staged subtitles by elapsed-time bucket. Evenly spaced across
      // the expected wall clock — last stage holds once reached.
      const subs = subtitlesRef.current;
      if (subs && subs.length > 1) {
        const bucket = expectedDurationMs / subs.length;
        const idx = Math.min(subs.length - 1, Math.floor(elapsed / bucket));
        setStageIdx(idx);
      }
    }, 80);

    return () => window.clearInterval(tick);
  }, [expectedDurationMs, hasProgressOverride]);

  const displaySubtitle = subtitles && subtitles.length > 0
    ? subtitles[stageIdx]
    : subtitle;

  const progress$ = hasProgressOverride
    ? Math.max(0, Math.min(100, progressPctOverride ?? 0))
    : progress;
  const progressPct = Math.round(progress$);

  return (
    <div
      className="relative w-full h-full min-h-[400px] flex flex-col items-center justify-center overflow-hidden rounded-2xl"
      style={{ animation: 'ws-fadein 400ms ease-out both' }}
    >
      {/* Layer 1: drifting gradient backdrop. The same three accent colors
          StepPreview uses, kept consistent so wizard surfaces feel like one
          family. */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background:
            'linear-gradient(135deg, rgba(244,114,182,0.18), rgba(99,102,241,0.18), rgba(56,189,248,0.18), rgba(244,114,182,0.18))',
          backgroundSize: '300% 300%',
          animation: 'ws-bgshift 8s ease-in-out infinite',
        }}
      />

      {/* Layer 2: shimmer band sweep across the full surface */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
          backgroundSize: '200% 100%',
          animation: 'ws-shimmer 2.4s linear infinite',
        }}
      />

      {/* Layer 3: floating sparkle particles */}
      <div className="absolute inset-0 pointer-events-none">
        {SPARKLES.map((p, i) => (
          <i
            key={i}
            className="ri-sparkling-2-fill text-primary absolute"
            style={{
              top: p.top,
              left: p.left,
              fontSize: `${p.size * 2}px`,
              animation: `ws-float ${p.dur} ease-in-out ${p.delay} infinite, ws-twinkle ${p.dur} ease-in-out ${p.delay} infinite`,
              opacity: 0.7,
            }}
          />
        ))}
      </div>

      {/* Layer 4: center cluster — 112px progress ring + percentage + title */}
      <div className="relative flex flex-col items-center gap-4 pointer-events-none">
        <div className="relative w-28 h-28">
          <svg className="w-28 h-28 -rotate-90" viewBox="0 0 112 112">
            <defs>
              <linearGradient id="wsProgressGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f472b6" />
                <stop offset="50%" stopColor="#6366f1" />
                <stop offset="100%" stopColor="#38bdf8" />
              </linearGradient>
            </defs>
            <circle
              cx="56"
              cy="56"
              r="48"
              fill="none"
              stroke="rgba(0,0,0,0.08)"
              strokeWidth="8"
            />
            <circle
              cx="56"
              cy="56"
              r="48"
              fill="none"
              stroke="url(#wsProgressGrad)"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 48}
              strokeDashoffset={2 * Math.PI * 48 * (1 - progress$ / 100)}
              style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
            />
          </svg>
          {/* Center sparkle — slow spin + twinkle so the ring isn't static
              during the long stretches between percentage updates. */}
          <div className="absolute inset-0 flex items-center justify-center">
            <i
              className="ri-sparkling-2-fill text-primary text-3xl"
              style={{
                animation:
                  'ws-spin 4s linear infinite, ws-twinkle 1.6s ease-in-out infinite',
              }}
            />
          </div>
        </div>

        <div className="text-center px-6">
          <p className="font-display font-bold text-dark text-base">{title}</p>
          {displaySubtitle ? (
            <p
              key={displaySubtitle}
              className="text-sm text-dark-muted mt-2"
              style={{ animation: 'ws-fadein 400ms ease-out both' }}
            >
              {displaySubtitle}
            </p>
          ) : null}
          <p className="font-display font-bold text-dark text-xl mt-3 tabular-nums">
            {progressPct}%
          </p>
        </div>
      </div>

      {/* Horizontal progress bar pinned near the bottom */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-[240px]">
        <div className="h-1.5 bg-surface-dark/20 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${progressPct}%`,
              background: 'linear-gradient(90deg, #f472b6, #6366f1, #38bdf8)',
              transition: 'width 400ms ease-out',
            }}
          />
        </div>
      </div>

      {/* Keyframes scoped via inline <style> — same pattern StepPreview uses.
          ws-* prefix keeps these isolated from sp-* (StepPreview) and wai-*
          (AiResultViewer) when multiple animated surfaces share a page. */}
      <style>{`
        @keyframes ws-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes ws-bgshift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes ws-float   { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        @keyframes ws-twinkle { 0%,100% { opacity: 0.35; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.15); } }
        @keyframes ws-spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes ws-fadein  { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
}
